import type { AdcampaignPlaybackReportTypeForInput } from '@shared/api/graphql/generated';

import {
  REPORT_PERIOD_INVALID_REASON,
  getReportPeriodInvalidReason,
  type ReportPeriodBounds,
} from '../ReportRequestForm/reportPeriod';
import { BACKEND_ERROR_MESSAGES } from './consts';
import { TEXTS } from './texts';

export { getReportAddressTags, mapPointToReportAddress } from '../ReportRequestForm/reportAddresses';

type ReportRequestErrorType = {
  messages?: string[] | null;
} | null;

const isPositiveFiniteNumber = (value: number) => Number.isFinite(value) && value > 0;

const REPORT_NOTIFICATION_TITLE_BY_REPORT_TYPE: Record<AdcampaignPlaybackReportTypeForInput, string> = {
  audio_ads: TEXTS.AUDIO_ADS_REPORT_NOTIFICATION_TITLE,
  video_ads: TEXTS.VIDEO_ADS_REPORT_NOTIFICATION_TITLE,
  video_waves: TEXTS.VIDEO_WAVES_REPORT_NOTIFICATION_TITLE,
};

export const getReportRequestSuccessNotification = (reportType: AdcampaignPlaybackReportTypeForInput): string =>
  TEXTS.REQUEST_REPORT_SUCCESS_NOTIFICATION(REPORT_NOTIFICATION_TITLE_BY_REPORT_TYPE[reportType]);

export const getReportRequestExistsNotification = (reportType: AdcampaignPlaybackReportTypeForInput): string =>
  TEXTS.REQUEST_REPORT_EXISTS_NOTIFICATION(REPORT_NOTIFICATION_TITLE_BY_REPORT_TYPE[reportType]);

export const validateDateRange = (start: Date, end: Date, bounds: ReportPeriodBounds): string | null => {
  const invalidReason = getReportPeriodInvalidReason(start, end, bounds);

  if (invalidReason === REPORT_PERIOD_INVALID_REASON.START_AFTER_END) {
    return TEXTS.WRONG_RANGE_START_MORE_END_ERROR;
  }

  return invalidReason ? TEXTS.REPORT_PERIOD_ERROR : null;
};

export const getReportRequestErrorMessage = (message?: string | null): string | null => {
  if (!message) {
    return null;
  }

  if (message.includes(BACKEND_ERROR_MESSAGES.AD_REPORT_PERMISSION_DENIED)) {
    return TEXTS.REQUEST_REPORT_ERROR_NO_ADS_NOTIFICATION;
  }

  if (message.includes(BACKEND_ERROR_MESSAGES.TOO_MANY_RECIPIENTS)) {
    return TEXTS.REPORT_RECIPIENTS_MAX_COUNT_ERROR;
  }

  if (message.includes(BACKEND_ERROR_MESSAGES.TOO_MANY_EXTERNAL_RECIPIENTS)) {
    return TEXTS.EXTERNAL_RECIPIENTS_MAX_COUNT_ERROR;
  }

  if (message.includes(BACKEND_ERROR_MESSAGES.INCORRECT_RECIPIENTS)) {
    return TEXTS.REPORT_RECIPIENTS_BACKEND_INVALID_ERROR;
  }

  if (message.includes(BACKEND_ERROR_MESSAGES.TOO_MANY_POINT_IDS)) {
    return TEXTS.REPORT_ADDRESSES_MAX_COUNT_ERROR;
  }

  if (message.includes(BACKEND_ERROR_MESSAGES.INCORRECT_POINT_IDS)) {
    return TEXTS.REPORT_ADDRESSES_BACKEND_INVALID_ERROR;
  }

  return null;
};

export const getReportRequestErrorsMessage = (errors?: ReportRequestErrorType[] | null): string | null => {
  const messages = errors?.flatMap((error) => error?.messages || []) || [];

  return messages.reduce<string | null>((message, currentMessage) => {
    if (message) {
      return message;
    }

    return getReportRequestErrorMessage(currentMessage);
  }, null);
};

export const getNumericPointIds = (addressIds: string[]): number[] =>
  addressIds.map((addressId) => Number(addressId)).filter(isPositiveFiniteNumber);

export const areStringArraysEqual = (firstArray: string[], secondArray: string[]): boolean => {
  if (firstArray.length !== secondArray.length) {
    return false;
  }

  return firstArray.every((value, index) => value === secondArray[index]);
};
